import type { Channel } from "../shared/host";
import { logEvent } from "../telemetry";
import type { ChatMessage } from "./types";

// AIVA-5171: chat history lived only in React state, so closing and reopening
// the Outlook side panel remounted the task pane and wiped the conversation.
// Persist the visible history keyed per host, and restore it on mount.
//
// AIVA-6685 (V-08): the transcript can contain PII / confidential content, so it
// is persisted in `sessionStorage` rather than `localStorage` — cleared when the
// taskpane window closes and not left in persistent storage for a same-origin
// XSS to read. The trade-off is that the conversation is no longer restored
// across a pane *reopen* (a fresh window starts with empty sessionStorage); it
// is re-hydrated from the server thread store, which is the source of truth.
//
// AIVA-5796: keying by host alone meant every Word/PowerPoint document (and
// every Outlook email) shared one bucket, so opening a different document
// restored the previous one's chat. The key now also carries a per-context id
// (the document URL for Word/PowerPoint, the mail item id for Outlook) so each
// document / email keeps its own conversation. A null contextId falls back to
// the legacy host-only key (e.g. an unsaved document with no URL, or standalone).
//
// AIVA-5929 (chat v2): the payload is schemaVersion 2 and carries the SERVER
// thread id instead of the old client-minted sessionId. The server thread
// store is the source of truth for the transcript; this cache exists only so
// the pane renders instantly on reopen while hydration reconciles. Legacy v1
// payloads still load as a render cache, but their sessionId is NOT a server
// thread id — it is discarded and threadId comes back null, so the next send
// creates a fresh server thread.

const STORAGE_KEY_PREFIX = "aiva_chat_history";

const CLEARED_KEY_PREFIX = "aiva_chat_cleared";

const CLEARED_REASON_READ_ERROR = "chat_cleared_read_error";
const CLEARED_REASON_WRITE_ERROR = "chat_cleared_write_error";

const SCHEMA_VERSION = 2 as const;

// Keep the stored payload well under the ~5MB sessionStorage budget — only the
// most recent turns matter for continuity, and an unbounded history would
// eventually overflow the quota and throw on every save.
const MAX_PERSISTED_MESSAGES = 100;

export interface PersistedChat {
  messages: ChatMessage[];
  /** Server thread id (chat v2). Null until the backend mints one. */
  threadId: string | null;
  msgCounter: number;
  schemaVersion: 2;
}

/** Legacy v1 payload shape, accepted on load for backwards compatibility. */
interface LegacyPersistedChat {
  messages?: unknown;
  sessionId?: unknown;
  msgCounter?: unknown;
}

function storageKey(host: Channel | null, contextId: string | null): string {
  const base = `${STORAGE_KEY_PREFIX}:${host ?? "default"}`;
  return contextId ? `${base}:${contextId}` : base;
}

function clearedKey(host: Channel | null, contextId: string | null): string {
  const base = `${CLEARED_KEY_PREFIX}:${host ?? "default"}`;
  return contextId ? `${base}:${contextId}` : base;
}

function reportClearedMarkError(
  reason: typeof CLEARED_REASON_READ_ERROR | typeof CLEARED_REASON_WRITE_ERROR,
): void {
  logEvent("connector.action.invoke", "warn", { connectorSystemName: "chat-cleared-mark", reason });
}

export function setChatCleared(host: Channel | null, contextId: string | null, cleared: boolean): void {
  try {
    if (cleared) localStorage.setItem(clearedKey(host, contextId), "1");
    else localStorage.removeItem(clearedKey(host, contextId));
  } catch {
    reportClearedMarkError(CLEARED_REASON_WRITE_ERROR);
  }
}

export function wasChatCleared(host: Channel | null, contextId: string | null = null): boolean {
  try {
    return localStorage.getItem(clearedKey(host, contextId)) === "1";
  } catch {
    reportClearedMarkError(CLEARED_REASON_READ_ERROR);
    return false;
  }
}

export function loadChatHistory(host: Channel | null, contextId: string | null = null): PersistedChat | null {
  try {
    const stored = sessionStorage.getItem(storageKey(host, contextId));
    if (!stored) return null;
    const parsed = JSON.parse(stored) as (Partial<PersistedChat> & LegacyPersistedChat) | null;
    if (!parsed || !Array.isArray(parsed.messages)) return null;
    const msgCounter = typeof parsed.msgCounter === "number" ? parsed.msgCounter : parsed.messages.length;
    if (parsed.schemaVersion === SCHEMA_VERSION) {
      return {
        messages: parsed.messages,
        threadId: typeof parsed.threadId === "string" ? parsed.threadId : null,
        msgCounter,
        schemaVersion: SCHEMA_VERSION,
      };
    }
    // Legacy v1 payload (client-minted sessionId). The transcript is still a
    // valid render cache, but the sessionId is NOT a server thread id — never
    // send it. threadId=null makes the next send create a fresh server thread.
    if (typeof parsed.sessionId !== "string") return null;
    return {
      messages: parsed.messages,
      threadId: null,
      msgCounter,
      schemaVersion: SCHEMA_VERSION,
    };
  } catch {
    // Corrupt or unavailable storage — start fresh rather than crash.
    return null;
  }
}

export function saveChatHistory(
  host: Channel | null,
  state: Omit<PersistedChat, "schemaVersion">,
  contextId: string | null = null,
): void {
  try {
    const trimmed: PersistedChat = {
      ...state,
      messages: state.messages.slice(-MAX_PERSISTED_MESSAGES),
      schemaVersion: SCHEMA_VERSION,
    };
    sessionStorage.setItem(storageKey(host, contextId), JSON.stringify(trimmed));
  } catch {
    /* ignore: persistence is best-effort, never block the chat on it */
  }
}

export function clearChatHistory(host: Channel | null, contextId: string | null = null): void {
  try {
    sessionStorage.removeItem(storageKey(host, contextId));
  } catch {
    /* ignore */
  }
}

/**
 * AIVA-6685 (V-08): one-time migration — remove any chat transcripts a
 * pre-upgrade session left in `localStorage` (every `aiva_chat_history*` bucket,
 * across hosts and per-context keys), so the fix clears existing exposure rather
 * than only redirecting future writes. No-op once none remain; safe to re-call.
 */
export function purgeLegacyLocalChatHistory(): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX)) stale.push(key);
    }
    for (const key of stale) localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
